import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import {Badge, Box, CircularProgress, Divider, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import {
    AggregationSurveyHealthSummary,
    SurveyIntroductionMetric,
    SurveySeriesPart,
} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {surveyColorOf} from "crqlar-react-common/dist/utils/surveyColor.util";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useSearchParams} from "react-router-dom";
import {useGuestCategories} from "../../guestCategories/useGuestCategories";
import {comparisonOf} from "../../insights/kit/Headline";
import {InsightsLoading} from "../../insights/kit/insightsLoading";
import {StatCard, StatCardData} from "../../insights/kit/StatCard";
import InsightsDateRangeControl from "../../insights/v2/base/InsightsDateRangeControl";
import {RadioFilterPanel} from "../../layout/rightSidebar/RadioFilterPanel";
import {RightSidebarFloatingPanel} from "../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../layout/rightSidebar/RightSidebarPanelHeader";
import {
    RightSidebarDivider,
    RightSidebarPanelButton,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import SurveyAnswersExportButton from "../evaluation/export/SurveyAnswersExportButton";
import SurveyEvaluationQuestionList from "../evaluation/SurveyEvaluationQuestionList";
import SurveyEvaluationSearch, {SurveyEvaluationSearchMode} from "../evaluation/SurveyEvaluationSearch";
import SurveyEvaluationSubmissionSearch from "../evaluation/SurveyEvaluationSubmissionSearch";
import SurveyQuestionSummaryCard from "../evaluation/SurveyQuestionSummaryCard";
import {CARD_GAP} from "../insights/constants";
import {toStatCards} from "../insights/data/surveyTargets.util";
import {useSurveyIntroduction, useSurveySeries} from "../insights/data/useSurveyInsightsQueries";
import {useSurveyAnswerContext} from "../shared/surveyAnswer.util";
import SurveyEvaluationFilters, {
    createDefaultEvaluationFilters,
    SurveyEvaluationFilterState,
} from "../shared/SurveyEvaluationFilters";
import {availableSurveyFilters, surveyFilterDescriptors} from "../shared/surveyFilters";
import {
    SurveyEvaluationScopeContext,
    useSurveyEvaluation,
    useSurveySectorOptions,
} from "../shared/useSurveyEvaluation";
import {useAllSurveys} from "../shared/useAllSurveys";
import {useSelectedSurveyId} from "../shared/useSelectedSurveyId";
import {urlDayRange} from "../shared/useUrlDateRange";

// A little wider than the settings page's 900, not the Insights tab's full 1440: the question cards read best as a column.
const CONTENT_MAX_WIDTH = 1100;

// The Insights band's own cards for the volume, so both tabs show the same figures, targets and comparisons.
const STAT_METRICS = [
    SurveyIntroductionMetric.INVITATIONS,
    SurveyIntroductionMetric.SUBMISSIONS,
    SurveyIntroductionMetric.RESPONSE_RATE,
];

// The completion rate comes from Survey health's counts, the same figure its card shows on Insights.
const HEALTH_PART = [SurveySeriesPart.HEALTH];

const percent = (value: number) => `${Math.round(value)}%`;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The Overview's "Completion rate" card, judged by the survey's completion target as Survey health judges it. */
function completionCard(summary: AggregationSurveyHealthSummary | undefined, t: Translate): StatCardData {
    const completion = summary?.completion ?? null;
    const target = summary?.target ?? null;

    return {
        id: 'Completion rate',
        title: t('Completion rate'),
        hint: t('Fill-outs that were sent off, as a share of the fill-outs that started in the period.'),
        value: completion == null ? '—' : percent(completion),
        comparison: comparisonOf(completion, summary?.previousCompletion, percent),
        target: completion == null || target == null || target <= 0 ? undefined : {
            label: t('Target {{value}}', {value: percent(target)}),
            progress: Math.round((completion / target) * 100),
            achieved: completion >= target,
        },
    };
}

/**
 * One survey's answers, question by question. The survey is picked on the rail and shared with the
 * Insights tab, so switching between the two keeps the survey you were looking at.
 */
export default function SurveysEvaluationTab() {
    useTrackScreenVisit('surveys evaluation');

    const {t} = useTranslation();
    const ctx = useSurveyAnswerContext();
    const {categories: guestCategories} = useGuestCategories();

    // A link can open the tab on a period (`from` / `to`), as the team email's "Open the evaluation" does.
    const [searchParams] = useSearchParams();
    const [filters, setFilters] = useState<SurveyEvaluationFilterState>(() => {
        const defaults = createDefaultEvaluationFilters();
        const linked = urlDayRange(searchParams);
        return linked ? {...defaults, range: linked} : defaults;
    });
    const [query, setQuery] = useState('');
    const [searchMode, setSearchMode] = useState<SurveyEvaluationSearchMode>('submissions');
    const questionQuery = searchMode === 'questions' ? query : '';
    const [isContentScrolled, setIsContentScrolled] = useState(false);

    const {surveys, isLoading: surveysLoading} = useAllSurveys();
    const {selectedSurveyId, selectSurvey} = useSelectedSurveyId(surveys);

    const surveyQuery = useQuery(
        ['survey', selectedSurveyId],
        () => SurveyService.getSurveyById(selectedSurveyId!),
        {enabled: !!selectedSurveyId, refetchOnWindowFocus: false}
    );
    const survey: Survey | undefined = surveyQuery.data;

    const sectorOptions = useSurveySectorOptions(survey);
    const {evaluation, loading, currentCaption, compareCaption, scope} = useSurveyEvaluation(selectedSurveyId, filters);

    const {introduction, loading: introductionLoading} = useSurveyIntroduction(selectedSurveyId, filters);
    const {series: healthSeries, loading: healthLoading} = useSurveySeries(selectedSurveyId, filters, HEALTH_PART);

    const stats = useMemo(
        () => [...toStatCards(introduction, t, STAT_METRICS), completionCard(healthSeries?.health?.summary, t)],
        [healthSeries, introduction, t]
    );

    // One state for the row: a skeleton while either request has nothing yet, dimmed while either refreshes.
    const statsLoading: InsightsLoading | undefined = introductionLoading === 'skeleton' || healthLoading === 'skeleton'
        ? 'skeleton'
        : introductionLoading ?? healthLoading;


    const pickSurvey = (surveyId: string) => {
        // Languages, sectors and versions are one survey's options. Carried over, they would narrow
        // the next survey with no filter left on screen to clear them.
        if (surveyId !== selectedSurveyId) {
            setFilters(current => ({...current, languages: [], sectorIds: [], versions: []}));
        }
        selectSurvey(surveyId);
    };

    const hasFilters = useMemo(
        () => availableSurveyFilters(surveyFilterDescriptors({
            languageOptions: survey?.languages ?? [],
            sectorOptions,
            currentVersion: survey?.version,
            guestCategories,
            t,
        })).length > 0,
        [survey?.languages, survey?.version, sectorOptions, guestCategories, t]
    );

    const activeFilterCount = [
        filters.languages.length > 0,
        filters.sectorIds.length > 0,
        filters.reviewStates.length > 0,
        filters.guestCategoryId != null,
        filters.versions.length > 0,
        filters.minSubmissions != null,
    ].filter(Boolean).length;

    const selectedSurvey = surveys.find(item => item.id === selectedSurveyId);

    const placeholder = (text: string) => (
        <Typography variant={'body2'} color={'text.secondary'} sx={{py: 6, textAlign: 'center'}}>
            {text}
        </Typography>
    );

    const content = () => {
        if (surveysLoading) {
            return (
                <Stack alignItems={'center'} sx={{py: 6}}>
                    <CircularProgress size={24}/>
                </Stack>
            );
        }

        if (!surveys.length) {
            return placeholder(t('No surveys yet. Create one in the settings to see its results here.'));
        }

        if (!survey) return <DetailsLoading/>;

        return (
            <Stack gap={CARD_GAP} sx={{width: '100%', maxWidth: CONTENT_MAX_WIDTH, mx: 'auto', minWidth: 0}}>
                {/* Four cards in one row from lg up: the kit's auto-filling grid would leave empty columns beside them at this width. */}
                <Box
                    sx={{
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))'},
                    }}
                >
                    {stats.map(item => <StatCard key={item.id} data={item} loading={statsLoading}/>)}
                </Box>

                <SurveyEvaluationSubmissionSearch
                    key={selectedSurveyId}
                    scope={scope}
                    query={searchMode === 'submissions' ? query : ''}
                    currentCaption={currentCaption}
                    compareCaption={compareCaption}
                    onClear={() => setQuery('')}
                />

                {!loading && (
                    <SurveyQuestionSummaryCard
                        survey={survey}
                        evaluation={evaluation}
                        ctx={ctx}
                        range={filters.range}
                        compareRange={filters.compareRange}
                        query={questionQuery}
                    />
                )}

                <SurveyEvaluationQuestionList
                    survey={survey}
                    evaluation={evaluation}
                    loading={loading}
                    ctx={ctx}
                    currentCaption={currentCaption}
                    compareCaption={compareCaption}
                    query={questionQuery}
                />
            </Stack>
        );
    };

    return (
        <SurveyEvaluationScopeContext.Provider value={scope}>
            <RightSidebarRailLayout>
                <RightSidebarRailHeader>
                    <RightSidebarRailHeaderCenter>
                        <InsightsDateRangeControl
                            range={filters.range}
                            compareRange={filters.compareRange}
                            onChange={selection => setFilters(current => ({
                                ...current,
                                range: selection.range,
                                compareRange: selection.compareRange,
                            }))}
                        />
                    </RightSidebarRailHeaderCenter>
                </RightSidebarRailHeader>

                <Divider sx={{borderColor: isContentScrolled ? 'divider' : 'transparent'}}/>

                <Box
                    onScroll={event => setIsContentScrolled(event.currentTarget.scrollTop > 0)}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        overflowY: 'auto',
                        pt: {xs: 2, md: 3},
                        pb: {xs: 6, md: 9},
                        px: {xs: 2, md: 3},
                    }}
                >
                    {content()}
                </Box>

                <RightSidebarRail>
                    <SurveyEvaluationSearch
                        query={query}
                        onQueryChange={setQuery}
                        mode={searchMode}
                        onModeChange={setSearchMode}
                    />

                    <RightSidebarPanelButton
                        icon={<PollOutlinedIcon/>}
                        label={selectedSurvey?.name || t('Survey')}
                        disabled={!surveys.length}
                        renderPanel={close => (
                            <RadioFilterPanel
                                title={t('Survey')}
                                options={surveys.map(item => ({value: item.id, label: item.name || item.id, color: surveyColorOf(item)}))}
                                value={selectedSurveyId ?? ''}
                                onChange={surveyId => {
                                    pickSurvey(surveyId);
                                    close();
                                }}
                                onClose={close}
                            />
                        )}
                    />

                    <RightSidebarPanelButton
                        icon={(
                            <Badge
                                color="primary"
                                badgeContent={activeFilterCount}
                                overlap="circular"
                                invisible={activeFilterCount === 0}
                            >
                                <FilterAltOutlinedIcon/>
                            </Badge>
                        )}
                        label={t('Filters')}
                        disabled={!hasFilters}
                        renderPanel={close => (
                            <RightSidebarFloatingPanel sx={{overflow: 'hidden'}}>
                                <RightSidebarPanelHeader title={t('Filters')} onClose={close}/>
                                <Stack sx={{flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 1, pb: 2}}>
                                    <SurveyEvaluationFilters
                                        value={filters}
                                        onChange={setFilters}
                                        languageOptions={survey?.languages ?? []}
                                        sectorOptions={sectorOptions}
                                        currentVersion={survey?.version}
                                        layout={'stacked'}
                                    />
                                </Stack>
                            </RightSidebarFloatingPanel>
                        )}
                    />

                    <RightSidebarDivider/>

                    <SurveyAnswersExportButton
                        survey={survey}
                        filters={filters}
                        sectorOptions={sectorOptions}
                        responseCount={evaluation?.responseCount}
                    />
                </RightSidebarRail>
            </RightSidebarRailLayout>
        </SurveyEvaluationScopeContext.Provider>
    );
}
